import { Link, Navigate, Route, Routes, useLocation } from 'react-router';
import { EmptyState, Skeleton } from '@agency/ui';
import { AccountPage } from './features/account/account-page.tsx';
import { AuditPage } from './features/audit/audit-page.tsx';
import { AcceptInvitePage, ForgotPasswordPage, ResetPasswordPage } from './features/auth/recovery.tsx';
import { LoginPage } from './features/auth/login.tsx';
import { SeoPage } from './features/seo/seo-page.tsx';
import { RolesPage } from './features/roles/roles-page.tsx';
import { SettingsPage } from './features/settings/settings-page.tsx';
import { DesignReviewPage } from './features/design/design-review.tsx';
import { EditorPage } from './features/landings/editor-page.tsx';
import { LandingsPage } from './features/landings/landings-page.tsx';
import { ContentEditorPage } from './features/content/content-editor.tsx';
import { ContentListPage } from './features/content/content-pages.tsx';
import { MediaPage } from './features/media/media-page.tsx';
import { LeadsPage } from './features/leads/leads-page.tsx';
import { PipelinePage } from './features/leads/pipeline-page.tsx';
import { DealsPage } from './features/leads/deals-page.tsx';
import { DirectoryPage } from './features/leads/directory-page.tsx';
import { PipelineStagesPage } from './features/leads/pipeline-stages-page.tsx';
import { TasksPage } from './features/leads/tasks-page.tsx';
import { AdvertisingPage } from './features/ads/advertising-page.tsx';
import { CampaignPage } from './features/ads/campaign-page.tsx';
import { WizardPage } from './features/ads/wizard-page.tsx';
import { OutcomesPage } from './features/outcomes/outcomes-page.tsx';
import { AnalyticsPage } from './features/outcomes/analytics-page.tsx';
import { TeamPage } from './features/team/team-page.tsx';
import { useAuth } from './lib/auth.tsx';
import { QueryError } from './lib/query-state.tsx';
import { NAV } from './nav.ts';
import { Shell } from './shell.tsx';
import type { ReactNode } from 'react';
import type { PermissionKey } from '@agency/contracts';

function Guard({ perm, children }: { perm: PermissionKey; children: ReactNode }) {
  const { can } = useAuth();
  if (!can(perm)) {
    return (
      <>
        <div className="pagehead"><div><h1>Restricted</h1><p>This area isn’t available to your role.</p></div></div>
        <EmptyState title="You don’t have access to this page" icon="lock" action={<Link to="/" className="ag-btn" style={{ textDecoration: 'none' }}>Back to your workspace</Link>}>
          Your role doesn’t include the permission this page needs. If you need it, ask an Owner or Administrator.
        </EmptyState>
      </>
    );
  }
  return <>{children}</>;
}

function Protected() {
  const { user, loading, error, refetch } = useAuth();
  const loc = useLocation();
  if (loading) return <div style={{ padding: 48, display: 'grid', gap: 12 }} role="status"><span className="ag-visually-hidden">Loading…</span><Skeleton width={220} height={24} /><Skeleton width="100%" height={200} /></div>;
  if (error) return <QueryError error={error} onRetry={refetch} />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  return <Shell />;
}

function Home() {
  const { can } = useAuth();
  const first = NAV.find((n) => can(n.perm) && (!n.extraPerm || can(n.extraPerm)));
  return first ? <Navigate to={first.to} replace /> : <EmptyState title="No areas available" icon="alert">Your role has no permitted destinations yet. Ask an Owner or Administrator.</EmptyState>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route element={<Protected />}>
        <Route index element={<Home />} />
        <Route path="leads" element={<Guard perm="leads.read"><LeadsPage /></Guard>} />
        <Route path="pipeline" element={<Guard perm="leads.read"><Guard perm="deals.read"><PipelinePage /></Guard></Guard>} />
        <Route path="deals" element={<Guard perm="deals.read"><DealsPage /></Guard>} />
        <Route path="tasks" element={<Guard perm="tasks.read"><Guard perm="leads.read"><Guard perm="leads.pii.read"><TasksPage /></Guard></Guard></Guard>} />
        <Route path="pipeline/stages" element={<Guard perm="deals.manage"><PipelineStagesPage /></Guard>} />
        <Route path="contacts" element={<Guard perm="leads.read"><Guard perm="leads.pii.read"><DirectoryPage kind="contacts" /></Guard></Guard>} />
        <Route path="companies" element={<Guard perm="leads.read"><Guard perm="leads.pii.read"><DirectoryPage kind="companies" /></Guard></Guard>} />
        <Route path="landings" element={<Guard perm="landings.read"><LandingsPage /></Guard>} />
        <Route path="landings/:variantId" element={<Guard perm="landings.read"><EditorPage /></Guard>} />
        <Route path="seo" element={<Guard perm="seo.read"><SeoPage /></Guard>} />
        <Route path="case-studies" element={<Guard perm="content.read"><ContentListPage type="CASE_STUDY" /></Guard>} />
        <Route path="articles" element={<Guard perm="content.read"><ContentListPage type="ARTICLE" /></Guard>} />
        <Route path="content/:id" element={<Guard perm="content.read"><ContentEditorPage /></Guard>} />
        <Route path="media" element={<Guard perm="content.read"><MediaPage /></Guard>} />
        <Route path="advertising" element={<Guard perm="ads.read"><AdvertisingPage /></Guard>} />
        <Route path="advertising/new" element={<Guard perm="ads.read"><Guard perm="ads.edit"><WizardPage /></Guard></Guard>} />
        <Route path="advertising/:id" element={<Guard perm="ads.read"><CampaignPage /></Guard>} />
        <Route path="advertising/:id/edit" element={<Guard perm="ads.read"><Guard perm="ads.edit"><WizardPage /></Guard></Guard>} />
        <Route path="outcomes" element={<Guard perm="events.read"><OutcomesPage /></Guard>} />
        <Route path="analytics" element={<Guard perm="analytics.read"><AnalyticsPage /></Guard>} />
        <Route path="team" element={<Guard perm="users.read"><TeamPage /></Guard>} />
        <Route path="roles" element={<Guard perm="roles.read"><RolesPage /></Guard>} />
        <Route path="audit" element={<Guard perm="audit.read"><AuditPage /></Guard>} />
        <Route path="settings" element={<Guard perm="settings.read"><SettingsPage /></Guard>} />
        <Route path="account" element={<AccountPage />} />
        {import.meta.env.DEV ? <Route path="design-review" element={<DesignReviewPage />} /> : null}
        <Route path="*" element={<EmptyState title="Page not found" icon="search">Check the address or use the sidebar.</EmptyState>} />
      </Route>
    </Routes>
  );
}
